// /admin/cobros-mercadopago — Vista consolidada de TODOS los cobros procesados por MP.
// Fuente única de verdad del cash flow real de Limpack SAS (tienda + suscripciones + ML + warmup).
// Sync automático cada 6hs desde MP prod via edge function mp-sincronizar-pagos.

import { useEffect, useMemo, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const CANAL_LABEL = {
  tienda: '🛒 Tienda',
  suscripcion: '🔁 Suscripción',
  preapproval: '📅 Preapproval',
  ml: '🟡 Mercado Libre',
  warmup: '🧪 Warmup',
  otro: '· Otro',
}
const CANAL_COLOR = {
  tienda: { bg: '#DCFCE7', fg: '#166534' },
  suscripcion: { bg: '#DBEAFE', fg: '#1E40AF' },
  preapproval: { bg: '#EDE9FE', fg: '#5B21B6' },
  ml: { bg: '#FEF9C3', fg: '#854D0E' },
  warmup: { bg: '#FEF3C7', fg: '#78350F' },
  otro: { bg: '#F1F5F9', fg: '#475569' },
}

const STATUS_LABEL = {
  approved: '✅ Aprobado',
  rejected: '❌ Rechazado',
  refunded: '↩ Devuelto',
  in_process: '⏳ En proceso',
  pending: '⌛ Pendiente',
  cancelled: '⊘ Cancelado',
}
const STATUS_COLOR = {
  approved:   { bg: '#DCFCE7', fg: '#166534' },
  rejected:   { bg: '#FEE2E2', fg: '#991B1B' },
  refunded:   { bg: '#FFE4E6', fg: '#9F1239' },
  in_process: { bg: '#FEF9C3', fg: '#854D0E' },
  pending:    { bg: '#F1F5F9', fg: '#475569' },
  cancelled:  { bg: '#F1F5F9', fg: '#475569' },
}

function fmtMoneda(n, moneda = 'UYU') {
  const s = Number(n || 0).toLocaleString('es-UY', { maximumFractionDigits: 2 })
  return moneda === 'UYU' ? `$${s}` : `${moneda} ${s}`
}
function fmtFecha(d) {
  if (!d) return '—'
  const dt = new Date(d)
  return dt.toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', year: '2-digit' })
}
function fmtFechaHora(d) {
  if (!d) return '—'
  const dt = new Date(d)
  return dt.toLocaleString('es-UY', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

export default function CobrosMercadoPago() {
  const [pagos, setPagos] = useState([])
  const [loading, setLoading] = useState(true)
  const [sincronizando, setSincronizando] = useState(false)
  const [msg, setMsg] = useState(null)
  const [tab, setTab] = useState('todos') // todos | aprobados | por_liberar | rechazados | devueltos | sin_asiento
  const [canalFiltro, setCanalFiltro] = useState('todos')
  const [busqueda, setBusqueda] = useState('')
  const [mostrarPruebas, setMostrarPruebas] = useState(() => {
    try { return localStorage.getItem('limpack_cobros_mp_ver_pruebas') === '1' } catch { return false }
  })
  useEffect(() => {
    try { localStorage.setItem('limpack_cobros_mp_ver_pruebas', mostrarPruebas ? '1' : '0') } catch {}
  }, [mostrarPruebas])

  useEffect(() => { cargar() }, [])

  // Cuenta MP Limpack SAS · confirmada 17-jul-2026
  const CUENTA_MP = {
    titular: 'LIMPACK SAS',
    rut: '220903660013',
    numero: '1018034478080',
    alias: 'LIMPACK SAS',       // alias en MP · así lo buscan al transferir
    softDescriptor: 'LIMPACKUY', // cómo aparece en el resumen de tarjeta del comprador (distinto)
  }
  const [copiado, setCopiado] = useState(false)
  async function copiarCuenta() {
    const texto = `💳 *Pagá con MercadoPago*\n\n👤 Titular: *${CUENTA_MP.titular}*\n🔢 N° cuenta: *${CUENTA_MP.numero}*\n📛 Alias: *${CUENTA_MP.alias}*\n\nCualquier consulta a hola@limpack.uy 💚`
    try {
      await navigator.clipboard.writeText(texto)
      setCopiado(true)
      setTimeout(() => setCopiado(false), 2500)
    } catch (e) {
      alert('No se pudo copiar. Copiá manualmente: ' + CUENTA_MP.numero)
    }
  }

  async function cargar() {
    setLoading(true)
    const { data, error } = await supabase
      .from('mp_pagos_registro')
      .select('*')
      .order('fecha_aprobacion', { ascending: false, nullsFirst: false })
      .order('created_at', { ascending: false })
      .limit(500)
    if (error) { setMsg({ tipo: 'err', txt: error.message }); setLoading(false); return }
    setPagos(data || [])
    setLoading(false)
  }

  async function sincronizar() {
    setSincronizando(true); setMsg(null)
    try {
      const r = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/mp-sincronizar-pagos`)
      const data = await r.json()
      if (!r.ok || data.error) throw new Error(data.error || 'error de sync')
      setMsg({ tipo: 'ok', txt: `✓ Sincronizados ${data.synced} pagos (${data.total_encontrados} encontrados)` })
      await cargar()
    } catch (e) {
      setMsg({ tipo: 'err', txt: 'Error: ' + e.message })
    }
    setSincronizando(false)
  }

  const hoy = new Date().toISOString().slice(0, 10)

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase()
    return pagos.filter(p => {
      if (!mostrarPruebas && p.es_prueba) return false
      if (canalFiltro !== 'todos' && p.canal !== canalFiltro) return false
      if (tab === 'aprobados' && p.status !== 'approved') return false
      if (tab === 'por_liberar' && !(p.status === 'approved' && !p.fecha_liberacion_real && p.fecha_liberacion_estimada && p.fecha_liberacion_estimada > hoy)) return false
      if (tab === 'rechazados' && p.status !== 'rejected') return false
      if (tab === 'devueltos' && p.status !== 'refunded') return false
      if (tab === 'sin_asiento' && !(p.status === 'approved' && !p.asiento_id)) return false
      if (tab === 'pendientes' && !['pending', 'in_process'].includes(p.status)) return false
      if (q) {
        const hay = [p.mp_payment_id, p.external_reference, p.payer_email, p.card_last_four, p.canal, p.metodo_pago].filter(Boolean).join(' ').toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
  }, [pagos, tab, canalFiltro, busqueda, mostrarPruebas, hoy])

  const totalPruebas = pagos.filter(p => p.es_prueba).length

  const kpis = useMemo(() => {
    const reales = pagos.filter(p => !p.es_prueba)
    const aprobados = reales.filter(p => p.status === 'approved')
    const brutoAprobado = aprobados.reduce((s, p) => s + Number(p.monto_bruto || 0), 0)
    const comisionTotal = aprobados.reduce((s, p) => s + Number(p.monto_comision || 0), 0)
    const netoTotal = aprobados.reduce((s, p) => s + Number(p.monto_neto || 0), 0)
    const porLiberar = aprobados.filter(p => !p.fecha_liberacion_real && p.fecha_liberacion_estimada && p.fecha_liberacion_estimada > hoy)
    const montoPorLiberar = porLiberar.reduce((s, p) => s + Number(p.monto_neto || 0), 0)
    const sinAsiento = aprobados.filter(p => !p.asiento_id).length
    const pctComision = brutoAprobado > 0 ? (comisionTotal / brutoAprobado) * 100 : 0
    return {
      totalPagos: reales.length,
      cantAprobados: aprobados.length,
      brutoAprobado,
      comisionTotal,
      netoTotal,
      cantPorLiberar: porLiberar.length,
      montoPorLiberar,
      sinAsiento,
      pctComision,
    }
  }, [pagos, hoy])

  const conteoTabs = useMemo(() => {
    const reales = pagos.filter(p => mostrarPruebas || !p.es_prueba)
    return {
      todos: reales.length,
      aprobados: reales.filter(p => p.status === 'approved').length,
      por_liberar: reales.filter(p => p.status === 'approved' && !p.fecha_liberacion_real && p.fecha_liberacion_estimada > hoy).length,
      pendientes: reales.filter(p => ['pending', 'in_process'].includes(p.status)).length,
      rechazados: reales.filter(p => p.status === 'rejected').length,
      devueltos: reales.filter(p => p.status === 'refunded').length,
      sin_asiento: reales.filter(p => p.status === 'approved' && !p.asiento_id).length,
    }
  }, [pagos, mostrarPruebas, hoy])

  const canalesPresentes = useMemo(() => {
    const set = new Set(pagos.map(p => p.canal).filter(Boolean))
    return Array.from(set).sort()
  }, [pagos])

  return (
    <AdminLayout>
      <div className="cmp-wrap">
        <header className="cmp-head">
          <div>
            <h1>Cobros MercadoPago</h1>
            <p>Vista consolidada del cash flow real · Tienda + Suscripciones + ML + Warmup</p>
          </div>
          <button className="cmp-btn-sync" onClick={sincronizar} disabled={sincronizando}>
            {sincronizando ? '⏳ Sincronizando…' : '🔄 Sincronizar ahora'}
          </button>
        </header>

        {/* Cuenta MP · para copiar y mandar por WhatsApp */}
        <section style={{
          margin: '12px 0 20px',
          padding: '14px 18px',
          background: 'linear-gradient(135deg, #009EE3 0%, #00B4D8 100%)',
          borderRadius: 12,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
          boxShadow: '0 4px 12px rgba(0, 158, 227, 0.25)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: '1 1 auto' }}>
            <div style={{
              width: 44, height: 44, borderRadius: 10, background: 'rgba(255,255,255,.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
            }}>💳</div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', opacity: .85 }}>
                Cuenta MercadoPago
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: '.02em', marginTop: 2 }}>
                {CUENTA_MP.numero}
              </div>
              <div style={{ fontSize: 12, opacity: .9, marginTop: 2 }}>
                {CUENTA_MP.titular} · Alias <strong>{CUENTA_MP.alias}</strong>
              </div>
            </div>
          </div>
          <button
            onClick={copiarCuenta}
            style={{
              padding: '10px 16px',
              background: copiado ? '#10B981' : '#fff',
              color: copiado ? '#fff' : '#009EE3',
              border: 0, borderRadius: 8,
              fontSize: 13, fontWeight: 800, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
              transition: 'all .2s',
              whiteSpace: 'nowrap',
            }}
          >
            {copiado ? '✓ Copiado' : '📋 Copiar para WhatsApp'}
          </button>
        </section>

        {msg && (
          <div className={`cmp-msg cmp-msg-${msg.tipo}`}>{msg.txt}
            <button onClick={() => setMsg(null)} className="cmp-msg-x">×</button>
          </div>
        )}

        {/* KPIs */}
        <section className="cmp-kpis">
          <Kpi label="Bruto cobrado" value={fmtMoneda(kpis.brutoAprobado)} sub={`${kpis.cantAprobados} pagos aprobados`} color="ok" />
          <Kpi label="Comisión MP" value={fmtMoneda(kpis.comisionTotal)} sub={`${kpis.pctComision.toFixed(2)}% promedio`} color="warn" />
          <Kpi label="Neto acreditado" value={fmtMoneda(kpis.netoTotal)} sub="Bruto − comisión" color="navy" />
          <Kpi label="Por liberar" value={fmtMoneda(kpis.montoPorLiberar)} sub={`${kpis.cantPorLiberar} pagos con fecha futura`} color={kpis.cantPorLiberar > 0 ? 'warn' : 'ok'} />
          <Kpi label="Sin asiento Zeta" value={kpis.sinAsiento} sub="Requieren contabilizar" color={kpis.sinAsiento > 0 ? 'danger' : 'ok'} />
        </section>

        {/* Tabs */}
        <div className="cmp-tabs">
          {[
            ['todos', 'Todos'],
            ['aprobados', 'Aprobados'],
            ['por_liberar', 'Por liberar'],
            ['pendientes', 'Pendientes'],
            ['rechazados', 'Rechazados'],
            ['devueltos', 'Devueltos'],
            ['sin_asiento', '⚠ Sin asiento'],
          ].map(([id, label]) => (
            <button key={id} className={`cmp-tab ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>
              {label} <span className="cmp-tab-count">{conteoTabs[id] || 0}</span>
            </button>
          ))}
          {totalPruebas > 0 && (
            <button
              type="button"
              onClick={() => setMostrarPruebas(v => !v)}
              style={{
                marginLeft: 'auto', padding: '4px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                background: mostrarPruebas ? '#FEF3C7' : '#F1F5F9',
                color: mostrarPruebas ? '#78350F' : '#475569',
                border: `1px solid ${mostrarPruebas ? '#FCD34D' : '#CBD5E1'}`,
              }}
            >
              🧪 {mostrarPruebas ? `Ocultar pruebas (${totalPruebas})` : `Ver pruebas (${totalPruebas})`}
            </button>
          )}
        </div>

        {/* Filtros */}
        <div className="cmp-filtros">
          <input className="cmp-search" placeholder="Buscar payment ID, email, tarjeta…"
            value={busqueda} onChange={e => setBusqueda(e.target.value)} />
          <select className="cmp-select" value={canalFiltro} onChange={e => setCanalFiltro(e.target.value)}>
            <option value="todos">Canal: todos</option>
            {canalesPresentes.map(c => <option key={c} value={c}>{CANAL_LABEL[c] || c}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="cmp-empty">Cargando…</div>
        ) : filtrados.length === 0 ? (
          <div className="cmp-empty">
            {pagos.length === 0
              ? 'Todavía no llegó ningún pago MP. Se sincroniza automático cada 6hs.'
              : 'No hay pagos con ese filtro.'}
          </div>
        ) : (
          <table className="cmp-tabla">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Canal</th>
                <th>Ref / Cliente</th>
                <th>Método</th>
                <th style={{ textAlign: 'right' }}>Bruto</th>
                <th style={{ textAlign: 'right' }}>Comisión</th>
                <th style={{ textAlign: 'right' }}>Neto</th>
                <th>Liberación</th>
                <th>Estado</th>
                <th>Zeta</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map(p => {
                const canalCfg = CANAL_COLOR[p.canal] || CANAL_COLOR.otro
                const statusCfg = STATUS_COLOR[p.status] || STATUS_COLOR.pending
                const liberado = p.fecha_liberacion_real
                const liberacionDias = p.fecha_liberacion_estimada
                  ? Math.ceil((new Date(p.fecha_liberacion_estimada + 'T12:00:00') - new Date()) / 86400000)
                  : null
                return (
                  <tr key={p.id} style={p.es_prueba ? { background: 'rgba(254,243,199,.35)' } : undefined}>
                    <td>
                      <div style={{ fontSize: 12 }}>{fmtFecha(p.fecha_aprobacion || p.fecha_creacion)}</div>
                      <div style={{ fontSize: 10, color: '#94A3B8' }}>{fmtFechaHora(p.fecha_aprobacion || p.fecha_creacion).split(' ')[1]}</div>
                    </td>
                    <td>
                      <span className="cmp-chip" style={{ background: canalCfg.bg, color: canalCfg.fg }}>
                        {CANAL_LABEL[p.canal] || p.canal}
                      </span>
                      {p.es_prueba && (
                        <div style={{ display: 'inline-block', marginLeft: 4, padding: '1px 5px', borderRadius: 4, fontSize: 9, fontWeight: 800, background: '#FEF3C7', color: '#78350F', border: '1px solid #FCD34D' }}>
                          PRUEBA
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize: 11.5 }}>
                      <div style={{ fontFamily: 'monospace', color: '#475569' }}>{p.external_reference || `MP:${p.mp_payment_id}`}</div>
                      {p.payer_email && <div style={{ color: '#94A3B8', fontSize: 10.5 }}>{p.payer_email}</div>}
                    </td>
                    <td style={{ fontSize: 11.5 }}>
                      <div>{p.metodo_pago || '—'}</div>
                      {p.card_last_four && <div style={{ color: '#94A3B8', fontSize: 10.5 }}>**** {p.card_last_four}</div>}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtMoneda(p.monto_bruto, p.moneda)}</td>
                    <td style={{ textAlign: 'right', color: '#DC2626' }}>−{fmtMoneda(p.monto_comision, p.moneda)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 800, color: '#059669' }}>{fmtMoneda(p.monto_neto, p.moneda)}</td>
                    <td style={{ fontSize: 11.5 }}>
                      {liberado ? (
                        <><span style={{ color: '#059669' }}>✓ Liberado</span><div style={{ color: '#94A3B8', fontSize: 10 }}>{fmtFecha(liberado)}</div></>
                      ) : p.fecha_liberacion_estimada ? (
                        <><span style={{ color: liberacionDias > 0 ? '#F59E0B' : '#059669' }}>
                          {liberacionDias > 0 ? `en ${liberacionDias}d` : 'hoy o antes'}
                        </span><div style={{ color: '#94A3B8', fontSize: 10 }}>{fmtFecha(p.fecha_liberacion_estimada)}</div></>
                      ) : '—'}
                    </td>
                    <td>
                      <span className="cmp-chip" style={{ background: statusCfg.bg, color: statusCfg.fg }}>
                        {STATUS_LABEL[p.status] || p.status}
                      </span>
                    </td>
                    <td>
                      {p.asiento_id ? (
                        <span style={{ fontSize: 11, color: '#059669' }}>✓ Asiento</span>
                      ) : p.status === 'approved' ? (
                        <span style={{ fontSize: 11, color: '#DC2626' }}>⚠ Falta</span>
                      ) : <span style={{ color: '#94A3B8' }}>—</span>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      <style>{styles}</style>
    </AdminLayout>
  )
}

function Kpi({ label, value, sub, color }) {
  const colorMap = {
    ok: '#059669', warn: '#F59E0B', danger: '#DC2626', navy: '#1A3461',
  }
  return (
    <div className="cmp-kpi">
      <div className="cmp-kpi-label">{label}</div>
      <div className="cmp-kpi-value" style={{ color: colorMap[color] || '#1A3461' }}>{value}</div>
      <div className="cmp-kpi-sub">{sub}</div>
    </div>
  )
}

const styles = `
.cmp-wrap { max-width: 1400px; margin: 0 auto; padding: 22px 18px 60px }
.cmp-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 20px }
.cmp-head h1 { margin: 0; font-size: 22px; color: #1A3461; letter-spacing: -.01em }
.cmp-head p { margin: 4px 0 0; color: #64748B; font-size: 13px }
.cmp-btn-sync {
  padding: 10px 18px; background: #1A3461; color: #fff; border: 0; border-radius: 8px;
  font-weight: 700; font-size: 13px; cursor: pointer;
}
.cmp-btn-sync:disabled { opacity: .6; cursor: default }
.cmp-msg { padding: 10px 14px; border-radius: 8px; margin-bottom: 14px; font-size: 13px; position: relative }
.cmp-msg-ok { background: #DCFCE7; color: #166534 }
.cmp-msg-err { background: #FEE2E2; color: #991B1B }
.cmp-msg-x { position: absolute; right: 8px; top: 4px; background: 0; border: 0; cursor: pointer; opacity: .5; font-size: 18px }

.cmp-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 12px; margin-bottom: 22px }
.cmp-kpi { background: #fff; padding: 14px 16px; border: 1px solid #E2E8F0; border-radius: 12px }
.cmp-kpi-label { font-size: 10.5px; font-weight: 800; letter-spacing: .05em; color: #94A3B8; text-transform: uppercase }
.cmp-kpi-value { font-size: 22px; font-weight: 900; margin: 4px 0; letter-spacing: -.02em }
.cmp-kpi-sub { font-size: 11px; color: #94A3B8 }

.cmp-tabs { display: flex; gap: 4px; margin-bottom: 12px; align-items: center; flex-wrap: wrap; border-bottom: 1px solid #E2E8F0; padding-bottom: 6px }
.cmp-tab {
  padding: 6px 12px; background: transparent; border: 0; border-bottom: 2px solid transparent;
  color: #64748B; font-weight: 600; font-size: 13px; cursor: pointer; transition: color .15s;
  display: inline-flex; align-items: center; gap: 6px;
}
.cmp-tab:hover { color: #1A3461 }
.cmp-tab.active { color: #1A3461; border-bottom-color: #1A3461 }
.cmp-tab-count {
  font-size: 10px; background: #F1F5F9; color: #475569; padding: 2px 6px; border-radius: 999px; font-weight: 700;
}
.cmp-tab.active .cmp-tab-count { background: #1A3461; color: #fff }

.cmp-filtros { display: flex; gap: 10px; margin-bottom: 14px; flex-wrap: wrap }
.cmp-search { flex: 1; min-width: 200px; padding: 8px 12px; border: 1px solid #CBD5E1; border-radius: 8px; font-size: 13px }
.cmp-select { padding: 8px 12px; border: 1px solid #CBD5E1; border-radius: 8px; font-size: 13px; background: #fff }

.cmp-tabla { width: 100%; border-collapse: separate; border-spacing: 0; background: #fff; border: 1px solid #E2E8F0; border-radius: 12px; overflow: hidden }
.cmp-tabla thead th {
  padding: 10px 12px; text-align: left; font-size: 11px; font-weight: 800; letter-spacing: .04em;
  color: #64748B; background: #F8FAFC; border-bottom: 1px solid #E2E8F0; text-transform: uppercase;
}
.cmp-tabla tbody td { padding: 10px 12px; font-size: 12.5px; border-bottom: 1px solid #F1F5F9; vertical-align: top }
.cmp-tabla tbody tr:last-child td { border-bottom: 0 }
.cmp-chip {
  display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 10.5px; font-weight: 700;
}

.cmp-empty { padding: 40px; text-align: center; color: #94A3B8; background: #fff; border: 1px dashed #CBD5E1; border-radius: 12px }
`
